/**
 * @author        nico@zumper.com (Nico Rattazzi)
 * @copyright     Copyright (c) 2017, Zumper
 * @description   Main Top level Component for the entire NZR application
 */

import { withSentryRouting } from '@sentry/react'
import { locationsEqual } from '@zcode/memoizers'
import { BodyClientSizeProvider } from '@zcode/nr-utils'
import { ON_BROWSER } from '@zcode/nr/es/shared/env'
import ScrollToTop from '@zcode/nr/es/shared/packages/scroll-to-top'
import { RoutesContext } from '@zcode/router-utils'
import type { MaybeEmpty } from '@zcode/utility-types'
import classNames from 'classnames'
import type { History, Location } from 'history'
import React from 'react'
import { Switch } from 'react-router-dom'
import { CompatRoute } from 'react-router-dom-v5-compat'
import { logger } from '../../logger'
import { default as routes } from '../../routes/index'
import { AllModals } from '../ModalWrapper/AllModals'
import { NotificationProvider } from '../Notification/NotificationProvider'
import { RenterSurveyWrapper } from '../RenterSurvey/RenterSurveyWrapper'
import { default as ButterBarContainer } from '../butter_bar/ButterBarContainer'

const SentryRoute = withSentryRouting(CompatRoute)

const stripScrollIgnoredFieldsFromLocation = (location: MaybeEmpty<Location>) => {
  return {
    ...location,
    hash: undefined,
    search: location.search?.replace(/gallery(=[^&]*)?/, '').replace(/^\?$/, ''),
  }
}

const shouldScrollToTop = (location: Location, prevLocation: MaybeEmpty<Location> = {}) => {
  return !locationsEqual(
    stripScrollIgnoredFieldsFromLocation(location),
    stripScrollIgnoredFieldsFromLocation(prevLocation)
  )
}

export interface AppProps {
  history: History
  isMobileAutocompleteOpen: boolean
  onBeforeunload: (event: BeforeUnloadEvent) => void
  onLocationChanged: (previousLocation?: Location) => void
  onMount: () => void
  onResize: () => void
  onSetOffline: () => void
  onSetOnline: () => void
  onStorage: (event: StorageEvent) => void
  updateNow: () => void
}

export class App extends React.PureComponent<AppProps> {
  unregisterHistoryListener?: (() => void) | null

  componentDidMount() {
    const {
      history,
      onBeforeunload,
      onMount,
      onSetOffline,
      onSetOnline,
      onResize,
      onStorage,
      onLocationChanged,
    } = this.props
    onResize && window.addEventListener('resize', onResize)
    onSetOffline && window.addEventListener('offline', onSetOffline)
    onSetOnline && window.addEventListener('online', onSetOnline)
    onStorage && window.addEventListener('storage', onStorage)
    onBeforeunload && window.addEventListener('beforeunload', onBeforeunload)
    onLocationChanged()
    onMount()

    // NOTE: this is for debugging the map location. See MainMap for more details.
    if (ON_BROWSER) {
      if (history.location.hash.includes('debug-map')) {
        // NOTE: When users disable cookies in their browser they also inadvertently disable
        // localStorage and sessionStorage. Checking for window.sessionStorage will throw in that
        // situation. Here we need to use a try/catch to avoid throwing.
        // https://michalzalecki.com/why-using-localStorage-directly-is-a-bad-idea/
        try {
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.setItem('debug-map', 'true')
          }
        } catch (error) {
          logger.debug('Unable to use sessionStorage', error)
        }
      }

      let previousLocation = history.location
      this.unregisterHistoryListener = history.listen((location) => {
        if (!locationsEqual(location, previousLocation)) {
          this.props.onLocationChanged(previousLocation)
        }
        previousLocation = location
      })

      window.addEventListener('zumper.updateNow', this.props.updateNow)
    }
  }

  componentWillUnmount() {
    const { onBeforeunload, onSetOffline, onSetOnline, onResize, onStorage } = this.props
    onResize && window.removeEventListener('resize', onResize)
    onSetOffline && window.removeEventListener('offline', onSetOffline)
    onSetOnline && window.removeEventListener('online', onSetOnline)
    onStorage && window.removeEventListener('storage', onStorage)
    onBeforeunload && window.removeEventListener('beforeunload', onBeforeunload)
    if (this.unregisterHistoryListener) {
      this.unregisterHistoryListener()
      this.unregisterHistoryListener = null
    }
    if (ON_BROWSER) {
      window.removeEventListener('zumper.updateNow', this.props.updateNow)
    }
  }

  render() {
    const { isMobileAutocompleteOpen } = this.props

    return (
      <RoutesContext.Provider value={routes}>
        <BodyClientSizeProvider>
          <NotificationProvider>
            <div
              className={classNames('h-full', isMobileAutocompleteOpen && 'fixed overflow-hidden')}
            >
              <ScrollToTop ignoreHashChange shouldScrollToTop={shouldScrollToTop} />
              <ButterBarContainer />
              <Switch>
                {routes.map((route) => (
                  <SentryRoute
                    {...route}
                    key={Array.isArray(route.path) ? route.path.join() : route.path}
                  />
                ))}
              </Switch>
              <AllModals />
              <RenterSurveyWrapper />
            </div>
          </NotificationProvider>
        </BodyClientSizeProvider>
      </RoutesContext.Provider>
    )
  }
}
